import { LIMONADE_EYE_REPORT_SCHEMA, LIMONADE_MOTIONLINK_SCHEMA, } from '@limonade/eye-core';
import { createTonalLuminanceReader, createPaletteColorTransform, tonalRangeWeights, TONAL_RANGES } from '@limonade/matching';
import { createColorTransform } from '@limonade/color-science';
import { createCubeLUT } from './CubeLUT.js';
export { PCEMatchAudit } from './PCEMatchAudit.js';
export { CompiledCubeLUT, applyCubeLUTToFrame, createCubeLUT } from './CubeLUT.js';
const round = (value, digits = 4) => Number(value.toFixed(digits));
const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
const radiansToDegrees = (value) => value * (180 / Math.PI);
const average = (values) => values.length ? round(values.reduce((sum, value) => sum + value, 0) / values.length) : null;
const spillRank = { unknown: -1, low: 0, medium: 1, high: 2 };
const motionPriority = [
    'shake', 'orbit_left', 'orbit_right', 'push_in', 'pull_out', 'pan_left', 'pan_right',
    'tilt_up', 'tilt_down', 'roll', 'handheld', 'static', 'unknown',
];
const FBX_TIME_TICKS_PER_SECOND = 46186158000;
function correctionSource(result) {
    return 'regionScores' in result ? 'scene-aware-pce' : 'pce';
}
function tuple(value) {
    return [round(value, 6), round(value, 6), round(value, 6)];
}
function escapeXml(value) {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&apos;');
}
function formatTuple(values) {
    return values.map((value) => round(value, 6).toFixed(6)).join(' ');
}
function rgbToHsv(rgb) {
    const [r, g, b] = rgb;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    let h = 0;
    if (delta > 1e-8) {
        if (max === r)
            h = ((g - b) / delta) % 6;
        else if (max === g)
            h = (b - r) / delta + 2;
        else
            h = (r - g) / delta + 4;
        h = (h * 60 + 360) % 360;
    }
    const s = max <= 0 ? 0 : delta / max;
    return [h, s, max];
}
function hsvToRgb(hsv) {
    const [h, s, v] = hsv;
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let rgb;
    if (h < 60)
        rgb = [c, x, 0];
    else if (h < 120)
        rgb = [x, c, 0];
    else if (h < 180)
        rgb = [0, c, x];
    else if (h < 240)
        rgb = [0, x, c];
    else if (h < 300)
        rgb = [x, 0, c];
    else
        rgb = [c, 0, x];
    return [rgb[0] + m, rgb[1] + m, rgb[2] + m];
}
function curveAt(curve, value) {
    if (!curve.length)
        return value;
    const position = clamp(value, 0, 1) * (curve.length - 1);
    const index = Math.floor(position);
    const next = Math.min(curve.length - 1, index + 1);
    const t = position - index;
    return (curve[index] ?? value) * (1 - t) + (curve[next] ?? value) * t;
}
function hueCurveAt(curve, hue) {
    if (!curve?.length)
        return 0;
    const position = ((hue % 360) + 360) % 360 / 360 * curve.length;
    const index = Math.floor(position) % curve.length;
    const next = (index + 1) % curve.length;
    const t = position - Math.floor(position);
    return (curve[index] ?? 0) * (1 - t) + (curve[next] ?? 0) * t;
}
function hueDistance(first, second) {
    return Math.abs(((first - second + 540) % 360) - 180);
}
function hueDelta(target, source) {
    return ((target - source + 540) % 360) - 180;
}
function averageCorrectionWarnings(result) {
    const warnings = [
        ...result.colorManagement.transformWarnings,
        ...(result.tonalAnalysis?.warnings ?? []),
        ...('warnings' in result ? result.warnings : []),
    ];
    return [...new Set(warnings)];
}
export function applyCDLToRGB(rgb, cdl) {
    const apply = (value, index) => {
        const sop = clamp(value * cdl.slope[index] + cdl.offset[index], 0, 1);
        return clamp(sop ** cdl.power[index], 0, 1);
    };
    const corrected = [
        apply(rgb[0], 0),
        apply(rgb[1], 1),
        apply(rgb[2], 2),
    ];
    const luma = corrected[0] * 0.2126 + corrected[1] * 0.7152 + corrected[2] * 0.0722;
    return [
        clamp(luma + (corrected[0] - luma) * cdl.saturation, 0, 1),
        clamp(luma + (corrected[1] - luma) * cdl.saturation, 0, 1),
        clamp(luma + (corrected[2] - luma) * cdl.saturation, 0, 1),
    ];
}
export function applyTargetedColorMatchesToRGB(rgb, matches = []) {
    if (!matches.length)
        return rgb;
    let [h, s, v] = rgbToHsv(rgb);
    if (s < 0.025 || v < 0.025)
        return rgb;
    for (const match of matches) {
        const [sourceH, sourceS, sourceV] = rgbToHsv(match.sourceRgb);
        const [targetH, targetS, targetV] = rgbToHsv(match.targetRgb);
        const hueRadius = match.hueRadiusDegrees ?? 34;
        const saturationRadius = match.saturationRadius ?? 0.42;
        const valueRadius = match.valueRadius ?? 0.5;
        const hueWeight = clamp(1 - hueDistance(h, sourceH) / hueRadius, 0, 1);
        const saturationWeight = clamp(1 - Math.abs(s - sourceS) / saturationRadius, 0, 1);
        const valueWeight = clamp(1 - Math.abs(v - sourceV) / valueRadius, 0, 1);
        const weight = hueWeight * saturationWeight * valueWeight * clamp(match.strength ?? 1, 0, 1.25);
        if (weight <= 0.001)
            continue;
        const hShift = clamp(hueDelta(targetH, sourceH), -48, 48) * weight;
        const sTarget = clamp(s * (1 - weight) + targetS * weight, 0, 1);
        const vTarget = clamp(v * (1 - weight * 0.72) + targetV * weight * 0.72, 0, 1);
        h = (h + hShift + 360) % 360;
        s = sTarget;
        v = vTarget;
    }
    return hsvToRgb([h, s, v]);
}
export function applyPCECubePreviewToRGB(rgb, cdl, options = {}) {
    return createPCECubePreviewTransform(cdl, options)(rgb);
}
/** Compile the tonal domain once for LUT/batch paths. Tone masks use the original input. */
export function createTonalHueTransform(correction) {
    const level = createTonalLuminanceReader(correction.workingColorSpace);
    const curves = TONAL_RANGES.map((range) => correction.ranges.find((entry) => entry.range === range && entry.confidence >= 0.25)?.hueVsHue);
    return (rgb) => {
        const luminance = level({ r: rgb[0], g: rgb[1], b: rgb[2] });
        if (luminance < 0.035 || luminance > 0.98)
            return rgb;
        const [h, s, v] = rgbToHsv(rgb);
        if (s < 0.035 || v <= 0)
            return rgb;
        const weights = tonalRangeWeights(luminance);
        let shift = 0;
        for (let index = 0; index < 3; index++)
            shift += hueCurveAt(curves[index], h) * weights[index];
        shift = clamp(shift, -14, 14) * clamp((s - 0.035) / 0.35, 0, 1);
        return hsvToRgb([(h + shift + 360) % 360, s, v]);
    };
}
export function createPCECubePreviewTransform(cdl, options = {}) {
    const tonal = options.tonalCorrection ? createTonalHueTransform(options.tonalCorrection) : undefined;
    return (rgb) => {
        let corrected = applyCDLToRGB(tonal ? tonal(rgb) : rgb, cdl);
        corrected = applyHueVectorCorrectionToRGB(corrected, options.hueVsHue, options.hueVsSat);
        return applyTargetedColorMatchesToRGB(corrected, options.targetedMatches);
    };
}
export class PCEToCDLConverter {
    convert(result) {
        if (result.suggestedCorrections.paletteCorrection)
            return {
                slope: [1, 1, 1], offset: [0, 0, 0], power: [1, 1, 1],
                saturation: result.suggestedCorrections.paletteCorrection.monochrome ? 0 : 1,
                confidence: result.confidence, metadata: { source: correctionSource(result), overallMatchScore: result.overallMatchScore,
                    limitations: ['Use CUBE for the exact audited palette look. ASC CDL cannot encode tone-conditioned palette colorization.',
                        result.suggestedCorrections.paletteCorrection.monochrome ? 'CDL saturation is zero; its encoded-domain luma differs from the linear-light monochrome CUBE.' : 'This identity CDL does not represent the palette look.'] },
            };
        const correction = result.suggestedCorrections;
        const exposureGain = 2 ** clamp(correction.exposureShift * 0.45, -0.5, 0.5);
        const contrastSlope = 1 + clamp(correction.contrastShift / 1200, -0.06, 0.06);
        const temperature = clamp(correction.temperatureShift / 3000, -0.055, 0.055);
        const tint = clamp(correction.tintShift / 700, -0.065, 0.065);
        const whitePointConfidence = clamp(correction.whitePoint?.confidence ?? 0, 0, 1);
        const whitePointStrength = clamp(whitePointConfidence * 0.92, 0, 0.92);
        const whitePointGains = correction.whitePoint?.channelGains ?? [1, 1, 1];
        const gainFromWhitePoint = whitePointGains.map((value) => clamp(1 + (value - 1) * whitePointStrength, 0.78, 1.28));
        const absoluteWhiteGains = correction.whitePoint
            ? [
                correction.whitePoint.reference.r / Math.max(1e-5, correction.whitePoint.source.r),
                correction.whitePoint.reference.g / Math.max(1e-5, correction.whitePoint.source.g),
                correction.whitePoint.reference.b / Math.max(1e-5, correction.whitePoint.source.b),
            ].map((value) => clamp(value, 0.62, 1.7))
            : [1, 1, 1];
        const whiteAnchorStrength = clamp(whitePointConfidence * 0.78, 0, 0.78);
        const residualStrength = 1 - whitePointStrength * 0.78;
        const residualTemperature = temperature * residualStrength;
        const residualTint = tint * residualStrength;
        const baseGain = exposureGain * contrastSlope;
        const anchoredSlope = (index, residualBalance) => {
            const exposureDriven = baseGain * gainFromWhitePoint[index] * residualBalance;
            const whiteDriven = absoluteWhiteGains[index] * (1 + clamp(correction.contrastShift / 1800, -0.035, 0.035));
            return exposureDriven * (1 - whiteAnchorStrength) + whiteDriven * whiteAnchorStrength;
        };
        const slope = [
            clamp(anchoredSlope(0, 1 + residualTemperature), 0.55, 1.65),
            clamp(anchoredSlope(1, 1 + residualTint * 0.5), 0.55, 1.65),
            clamp(anchoredSlope(2, 1 - residualTemperature), 0.55, 1.65),
        ].map((value) => round(value, 6));
        const blackPointConfidence = clamp(correction.blackPoint?.confidence ?? 0, 0, 1);
        const blackAnchorStrength = clamp(blackPointConfidence * 0.78, 0, 0.78);
        const blackPointOffset = correction.blackPoint
            ? [
                correction.blackPoint.reference.r - correction.blackPoint.source.r * slope[0],
                correction.blackPoint.reference.g - correction.blackPoint.source.g * slope[1],
                correction.blackPoint.reference.b - correction.blackPoint.source.b * slope[2],
            ].map((value) => clamp(value, -0.09, 0.09))
            : [0, 0, 0];
        const residualOffset = [
            clamp(residualTemperature * 0.08, -0.018, 0.018),
            clamp(residualTint * 0.44, -0.024, 0.024),
            clamp(-residualTemperature * 0.08, -0.018, 0.018),
        ];
        const offset = [
            clamp(residualOffset[0] * (1 - blackAnchorStrength) + blackPointOffset[0] * blackAnchorStrength, -0.06, 0.06),
            clamp(residualOffset[1] * (1 - blackAnchorStrength) + blackPointOffset[1] * blackAnchorStrength, -0.06, 0.06),
            clamp(residualOffset[2] * (1 - blackAnchorStrength) + blackPointOffset[2] * blackAnchorStrength, -0.06, 0.06),
        ].map((value) => round(value, 6));
        const contrastPowerValue = clamp(1 - correction.contrastShift / 1800, 0.82, 1.18);
        const skinToneConfidence = clamp(correction.skinToneMatch?.confidence ?? 0, 0, 1);
        const skinPowerStrength = clamp(skinToneConfidence * 0.68, 0, 0.68);
        const skinPower = correction.skinToneMatch?.channelPower ?? [contrastPowerValue, contrastPowerValue, contrastPowerValue];
        const power = [
            clamp(contrastPowerValue * (1 - skinPowerStrength) + skinPower[0] * skinPowerStrength, 0.78, 1.22),
            clamp(contrastPowerValue * (1 - skinPowerStrength) + skinPower[1] * skinPowerStrength, 0.78, 1.22),
            clamp(contrastPowerValue * (1 - skinPowerStrength) + skinPower[2] * skinPowerStrength, 0.78, 1.22),
        ].map((value) => round(value, 6));
        const saturationFromShift = clamp(1 + correction.saturationShift / 120, 0.65, 1.35);
        const saturationConfidence = clamp(correction.saturationMatch?.confidence ?? 0, 0, 1);
        const saturationFromMeasurement = clamp(correction.saturationMatch?.saturationRatio ?? saturationFromShift, 0.55, 1.55);
        const saturation = round(clamp(saturationFromShift * (1 - saturationConfidence * 0.72) + saturationFromMeasurement * saturationConfidence * 0.72, 0.55, 1.45), 6);
        return {
            slope,
            offset,
            power,
            saturation,
            confidence: round(result.confidence),
            metadata: {
                source: correctionSource(result),
                overallMatchScore: result.overallMatchScore,
                limitations: [
                    'Conservative ASC CDL approximation generated from PCE correction suggestions.',
                    correction.whitePoint
                        ? `White-point matching used ${correction.whitePoint.reference.sampleCount}/${correction.whitePoint.source.sampleCount} reference/source neutral samples at ${round(correction.whitePoint.confidence)} confidence.`
                        : 'No reliable white-point match was available; channel balance used temperature/tint fallback.',
                    correction.blackPoint
                        ? `Black-point matching used ${correction.blackPoint.reference.sampleCount}/${correction.blackPoint.source.sampleCount} reference/source shadow samples at ${round(correction.blackPoint.confidence)} confidence.`
                        : 'No reliable black-point match was available; shadow offset used conservative fallback.',
                    correction.skinToneMatch
                        ? `Skin/midtone matching used ${correction.skinToneMatch.reference.sampleCount}/${correction.skinToneMatch.source.sampleCount} reference/source samples at ${round(correction.skinToneMatch.confidence)} confidence.`
                        : 'Skin/midtone matching was unavailable or disabled.',
                    'Not a finishing-grade color transform.',
                    'Does not encode full tone-curve or hue-vector corrections.',
                ],
            },
        };
    }
}
export function exportCDL(cdl, options = {}) {
    const id = escapeXml(options.id ?? 'limonade_pce_cdl');
    const name = escapeXml(options.name ?? 'Limonade PCE CDL');
    return [
        '<?xml version="1.0" encoding="UTF-8"?>',
        `<ColorCorrection id="${id}">`,
        `  <SOPNode>`,
        `    <Slope>${formatTuple(cdl.slope)}</Slope>`,
        `    <Offset>${formatTuple(cdl.offset)}</Offset>`,
        `    <Power>${formatTuple(cdl.power)}</Power>`,
        `  </SOPNode>`,
        `  <SatNode>`,
        `    <Saturation>${round(cdl.saturation, 6).toFixed(6)}</Saturation>`,
        `  </SatNode>`,
        `  <Description>${name}</Description>`,
        `</ColorCorrection>`,
        '',
    ].join('\n');
}
export function exportCDLCollection(cdls, options = {}) {
    const name = escapeXml(options.name ?? 'Limonade PCE CDL Collection');
    const corrections = cdls.map((cdl, index) => exportCDL(cdl, {
        id: `${options.id ?? 'limonade_pce_cdl'}_${String(index + 1).padStart(3, '0')}`,
        name: `${name} ${index + 1}`,
    }).split('\n').slice(1).join('\n').trimEnd()).map((xml) => xml.split('\n').map((line) => `  ${line}`).join('\n'));
    return [
        '<?xml version="1.0" encoding="UTF-8"?>',
        `<ColorCorrectionCollection xmlns="urn:ASC:CDL:v1.2">`,
        ...corrections,
        `</ColorCorrectionCollection>`,
        '',
    ].join('\n');
}
/**
 * Resolve-oriented ASC CDL collection export.
 *
 * DaVinci Resolve workflows are generally safest with .cube LUTs, but ASC CDL
 * interchange commonly uses .cc for a single ColorCorrection and .ccc for a
 * ColorCorrectionCollection. This function is intentionally an alias with a
 * clearer name so UI/export surfaces can present a Resolve-friendly .ccc file.
 */
export function exportCCC(cdls, options = {}) {
    return exportCDLCollection(cdls, options);
}
export class LUTGenerator {
    generateCubeFromCDL(cdl, options = {}) {
        const size = options.size ?? 17;
        if (size !== 17 && size !== 33) {
            throw new Error('LUTGenerator supports only 17x17x17 and 33x33x33 LUTs.');
        }
        const title = options.title ?? 'pre match';
        const lines = [
            `TITLE "${title.replaceAll('"', "'")}"`,
            `LUT_3D_SIZE ${size}`,
            'DOMAIN_MIN 0.000000 0.000000 0.000000',
            'DOMAIN_MAX 1.000000 1.000000 1.000000',
        ];
        const denominator = size - 1;
        const preview = createPCECubePreviewTransform(cdl, options);
        for (let bIndex = 0; bIndex < size; bIndex++) {
            for (let gIndex = 0; gIndex < size; gIndex++) {
                for (let rIndex = 0; rIndex < size; rIndex++) {
                    const corrected = preview([rIndex / denominator, gIndex / denominator, bIndex / denominator]);
                    lines.push(corrected.map((value) => clamp(value, 0, 1).toFixed(6)).join(' '));
                }
            }
        }
        return `${lines.join('\n')}\n`;
    }
    generateCubeFromCDLStack(cdls, options = {}) {
        const size = options.size ?? 17;
        if (size !== 17 && size !== 33) {
            throw new Error('LUTGenerator supports only 17x17x17 and 33x33x33 LUTs.');
        }
        const title = options.title ?? 'pre match v2';
        const lines = [
            `TITLE "${title.replaceAll('"', "'")}"`,
            `LUT_3D_SIZE ${size}`,
            'DOMAIN_MIN 0.000000 0.000000 0.000000',
            'DOMAIN_MAX 1.000000 1.000000 1.000000',
        ];
        const denominator = size - 1;
        const tonal = options.tonalCorrection ? createTonalHueTransform(options.tonalCorrection) : undefined;
        for (let bIndex = 0; bIndex < size; bIndex++) {
            for (let gIndex = 0; gIndex < size; gIndex++) {
                for (let rIndex = 0; rIndex < size; rIndex++) {
                    const input = [rIndex / denominator, gIndex / denominator, bIndex / denominator];
                    const rgb = cdls.reduce((current, cdl) => applyCDLToRGB(current, cdl), tonal ? tonal(input) : input);
                    let corrected = this.applyHueVectorCorrection(rgb, options.hueVsHue, options.hueVsSat);
                    corrected = applyTargetedColorMatchesToRGB(corrected, options.targetedMatches);
                    lines.push(corrected.map((value) => clamp(value, 0, 1).toFixed(6)).join(' '));
                }
            }
        }
        return `${lines.join('\n')}\n`;
    }
    generateCube(context, options = {}) {
        const size = options.size ?? 17;
        if (size !== 17 && size !== 33) {
            throw new Error('LUTGenerator supports only 17x17x17 and 33x33x33 LUTs.');
        }
        const title = options.title ?? context.title ?? 'Limonade PCE Suggested Correction';
        const lines = [
            `TITLE "${title.replaceAll('"', "'")}"`,
            `LUT_3D_SIZE ${size}`,
            'DOMAIN_MIN 0.000000 0.000000 0.000000',
            'DOMAIN_MAX 1.000000 1.000000 1.000000',
        ];
        const denominator = size - 1;
        for (let bIndex = 0; bIndex < size; bIndex++) {
            for (let gIndex = 0; gIndex < size; gIndex++) {
                for (let rIndex = 0; rIndex < size; rIndex++) {
                    const rgb = this.applyCorrection([rIndex / denominator, gIndex / denominator, bIndex / denominator], context);
                    lines.push(rgb.map((value) => clamp(value, 0, 1).toFixed(6)).join(' '));
                }
            }
        }
        return `${lines.join('\n')}\n`;
    }
    applyCorrection(rgb, context) {
        const contrast = clamp(context.contrastShift / 250, -0.25, 0.25);
        const saturationScale = clamp(1 + context.saturationShift / 180, 0.45, 1.55);
        let corrected = [
            clamp((rgb[0] - 0.5) * (1 + contrast) + 0.5, 0, 1),
            clamp((rgb[1] - 0.5) * (1 + contrast) + 0.5, 0, 1),
            clamp((rgb[2] - 0.5) * (1 + contrast) + 0.5, 0, 1),
        ];
        const luma = corrected[0] * 0.2126 + corrected[1] * 0.7152 + corrected[2] * 0.0722;
        const targetLuma = curveAt(context.toneCurve, luma);
        const lumaDelta = clamp(targetLuma - luma, -0.12, 0.12);
        corrected = [
            clamp(corrected[0] + lumaDelta, 0, 1),
            clamp(corrected[1] + lumaDelta, 0, 1),
            clamp(corrected[2] + lumaDelta, 0, 1),
        ];
        let [h, s, v] = rgbToHsv(corrected);
        const hueIndex = Math.min(context.hueVsHue.length - 1, Math.max(0, Math.floor(h / 360 * context.hueVsHue.length)));
        const hueShift = clamp(context.hueVsHue[hueIndex] ?? 0, -12, 12);
        const hueSatShift = clamp(context.hueVsSat[hueIndex] ?? 0, -20, 20);
        h = (h + hueShift + 360) % 360;
        s = clamp(s * saturationScale + hueSatShift / 200, 0, 1);
        v = clamp(v, 0, 1);
        return hsvToRgb([h, s, v]);
    }
    applyHueVectorCorrection(rgb, hueVsHue, hueVsSat) {
        return applyHueVectorCorrectionToRGB(rgb, hueVsHue, hueVsSat);
    }
}
function applyHueVectorCorrectionToRGB(rgb, hueVsHue, hueVsSat) {
    if (!hueVsHue?.length && !hueVsSat?.length)
        return rgb;
    let [h, s, v] = rgbToHsv(rgb);
    if (s < 0.035 || v < 0.025)
        return rgb;
    const saturationWeight = clamp((s - 0.035) / 0.35, 0, 1);
    const hueShift = clamp(hueCurveAt(hueVsHue, h), -28, 28) * saturationWeight;
    const satShift = clamp(hueCurveAt(hueVsSat, h), -18, 18) / 180 * saturationWeight;
    h = (h + hueShift + 360) % 360;
    s = clamp(s + satShift, 0, 1);
    return hsvToRgb([h, s, v]);
}
export class LUTValidator {
    validateCube(cube) {
        const warnings = [];
        const lines = cube.split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line && !line.startsWith('#'));
        const sizeLine = lines.find((line) => line.startsWith('LUT_3D_SIZE '));
        const size = Number(sizeLine?.split(/\s+/)[1]);
        if (size !== 17 && size !== 33)
            warnings.push('Unsupported or missing LUT_3D_SIZE.');
        const table = lines
            .filter((line) => /^[-+]?\d*\.?\d+(?:\s+[-+]?\d*\.?\d+){2}$/.test(line))
            .map((line) => line.split(/\s+/).map(Number));
        if (size === 17 || size === 33) {
            const expected = size ** 3;
            if (table.length !== expected)
                warnings.push(`Malformed LUT table: expected ${expected} rows, found ${table.length}.`);
        }
        if (table.some((row) => row.length !== 3 || row.some((value) => !Number.isFinite(value)))) {
            warnings.push('Malformed LUT table values.');
        }
        if (table.some((row) => row.some((value) => value < 0 || value > 1))) {
            warnings.push('LUT contains values outside the normalized 0..1 range.');
        }
        if (size === 17 || size === 33) {
            for (let index = 1; index < Math.min(size, table.length); index++) {
                const previous = table[index - 1];
                const current = table[index];
                if ((current[0] ?? 0) + 0.08 < (previous[0] ?? 0)) {
                    warnings.push('LUT red axis appears non-monotonic.');
                    break;
                }
            }
        }
        return { valid: warnings.length === 0, warnings };
    }
}
export class GradeSuggestionEngine {
    suggest(result) {
        const correction = result.suggestedCorrections;
        const suggestions = [];
        if (Math.abs(correction.exposureShift) >= 0.05) {
            suggestions.push(`${correction.exposureShift > 0 ? 'Increase' : 'Reduce'} exposure by ${Math.abs(round(correction.exposureShift, 2))} stops`);
        }
        if (Math.abs(correction.tintShift) >= 2) {
            suggestions.push(correction.tintShift > 0 ? 'Reduce magenta tint / add green tint slightly' : 'Reduce green tint');
        }
        if (Math.abs(correction.temperatureShift) >= 2) {
            suggestions.push(correction.temperatureShift > 0 ? 'Warm the source image slightly' : 'Cool the source image slightly');
        }
        if (correction.whitePoint && correction.whitePoint.confidence >= 0.25) {
            const [r, g, b] = correction.whitePoint.channelGains;
            const strongest = Math.max(Math.abs(r - 1), Math.abs(g - 1), Math.abs(b - 1));
            if (strongest >= 0.015) {
                suggestions.push(`Match source white point using RGB channel gains ${r.toFixed(3)} / ${g.toFixed(3)} / ${b.toFixed(3)}`);
            }
        }
        if (correction.blackPoint && correction.blackPoint.confidence >= 0.25) {
            const [r, g, b] = correction.blackPoint.channelOffsets;
            const strongest = Math.max(Math.abs(r), Math.abs(g), Math.abs(b));
            if (strongest >= 0.006) {
                suggestions.push(`Match source black point using RGB offsets ${r.toFixed(3)} / ${g.toFixed(3)} / ${b.toFixed(3)}`);
            }
        }
        if (correction.saturationMatch && correction.saturationMatch.confidence >= 0.25 && Math.abs(correction.saturationMatch.saturationRatio - 1) >= 0.025) {
            suggestions.push(`${correction.saturationMatch.saturationRatio > 1 ? 'Increase' : 'Reduce'} saturation toward measured reference level (${correction.saturationMatch.saturationRatio.toFixed(3)}×)`);
        }
        if (correction.skinToneMatch && correction.skinToneMatch.confidence >= 0.25) {
            const [r, g, b] = correction.skinToneMatch.channelPower;
            const strongest = Math.max(Math.abs(r - 1), Math.abs(g - 1), Math.abs(b - 1));
            if (strongest >= 0.01) {
                suggestions.push(`Shape midtone skin color using CDL power ${r.toFixed(3)} / ${g.toFixed(3)} / ${b.toFixed(3)}`);
            }
        }
        if (Math.abs(correction.saturationShift) >= 2) {
            suggestions.push(`${correction.saturationShift > 0 ? 'Increase' : 'Reduce'} saturation by ${Math.abs(round(correction.saturationShift, 1))}%`);
        }
        if (Math.abs(correction.contrastShift) >= 2) {
            suggestions.push(`${correction.contrastShift > 0 ? 'Increase' : 'Reduce'} contrast conservatively`);
        }
        for (const band of result.tonalAnalysis?.bands ?? []) {
            if (band.confidence < 0.25)
                continue;
            if ((band.luminanceMatchScore ?? 1) < 0.8)
                suggestions.push(`Review ${band.range} brightness independently from the global exposure match`);
            if (band.colorVectors.vectors.some((vector) => vector.confidence >= 0.25 && Math.abs(vector.hueShiftDegrees) >= 3))
                suggestions.push(`Use tone-aware six-vector matching for color differences in ${band.range}`);
        }
        if ('regionScores' in result) {
            if ((result.regionScores.highlights ?? 1) < 0.82)
                suggestions.push('Highlights differ from reference; review highlight warmth and rolloff');
            if ((result.regionScores.skin ?? 1) < 0.84)
                suggestions.push('Skin tones need careful review before applying global corrections');
        }
        if (result.colorManagement.transformWarnings.length) {
            suggestions.push('Review color-management warnings before applying exports');
        }
        if (!suggestions.length)
            suggestions.push('No major grading correction recommended');
        return suggestions;
    }
}
export class PCEExportManager {
    cdl = new PCEToCDLConverter();
    lut = new LUTGenerator();
    suggestions = new GradeSuggestionEngine();
    generateCDL(result) {
        return this.cdl.convert(result);
    }
    generateCubeLUT(result, options = {}) {
        if (result.suggestedCorrections.paletteCorrection)
            return this.generatePaletteCube(result, options);
        return this.lut.generateCube({
            toneCurve: result.suggestedCorrections.toneCurve,
            hueVsHue: result.suggestedCorrections.hueVsHue,
            hueVsSat: result.suggestedCorrections.hueVsSat,
            saturationShift: result.suggestedCorrections.saturationShift,
            contrastShift: result.suggestedCorrections.contrastShift,
            title: options.title,
        }, options);
    }
    generateCDLCubeLUT(result, options = {}) {
        if (result.suggestedCorrections.paletteCorrection)
            return this.generatePaletteCube(result, options);
        const tonalCorrection = options.tonalCorrection ?? result.suggestedCorrections.tonalCorrection;
        return this.lut.generateCubeFromCDL(this.generateCDL(result), {
            size: options.size ?? 17,
            title: options.title ?? 'pre match',
            // Tonal hue curves replace global hue only. They contain no saturation
            // correction, so enabling tonal analysis must not erase hue-vs-sat.
            hueVsHue: options.hueVsHue ?? (tonalCorrection ? undefined : result.suggestedCorrections.colorVectorMatch?.hueVsHueCurve ?? result.suggestedCorrections.hueVsHue),
            hueVsSat: options.hueVsSat ?? result.suggestedCorrections.hueVsSat,
            tonalCorrection,
            targetedMatches: options.targetedMatches,
        });
    }
    generateCDLStackCubeLUT(cdls, options = {}) {
        return this.lut.generateCubeFromCDLStack(cdls, {
            size: options.size ?? 17,
            title: options.title ?? 'pre match v2',
            hueVsHue: options.hueVsHue,
            hueVsSat: options.hueVsSat,
            targetedMatches: options.targetedMatches,
        });
    }
    generatePaletteCube(result, options) {
        const transform = createPaletteColorTransform(result.suggestedCorrections.paletteCorrection);
        const working = result.colorManagement.workingColorSpace === 'OKLab-analysis-from-sRGB' ? 'sRGB' : result.colorManagement.workingColorSpace;
        const toDisplay = working === 'sRGB' ? undefined : createColorTransform({ from: working, to: 'sRGB' });
        const toWorking = working === 'sRGB' ? undefined : createColorTransform({ from: 'sRGB', to: working });
        return createCubeLUT((rgb) => {
            const raw = { r: rgb[0], g: rgb[1], b: rgb[2] };
            const color = transform(toDisplay?.rgb(raw) ?? raw);
            const output = toWorking?.rgb(color) ?? color;
            return result.suggestedCorrections.paletteCorrection.monochrome ? [output.r, output.g, output.b]
                : applyTargetedColorMatchesToRGB([output.r, output.g, output.b], options.targetedMatches);
        }, options.size ?? 17, options.title ?? 'pre match palette');
    }
    generateReport(result, options = {}) {
        const size = options.size ?? 17;
        return {
            schema: 'limonade-pce-grading-export/v1',
            created_at: new Date().toISOString(),
            cdl: this.generateCDL(result),
            lutMetadata: { size, type: 'CUBE_3D' },
            metadata: {
                overallMatchScore: result.overallMatchScore,
                hueMatchScore: result.hueMatchScore,
                saturationMatchScore: result.saturationMatchScore,
                luminanceMatchScore: result.luminanceMatchScore,
                confidence: result.confidence,
                colorManagement: result.colorManagement,
                semanticAnalysis: 'semanticAnalysis' in result ? result.semanticAnalysis : undefined,
                tonalAnalysis: result.tonalAnalysis,
                paletteAnalysis: result.paletteAnalysis,
                warnings: averageCorrectionWarnings(result),
                limitations: [
                    'Deterministic grading suggestion export generated from PCE corrections.',
                    'Not a finishing-grade color engine.',
                    'No OCIO, Resolve, film-emulation, or camera-log transform is applied.',
                ],
            },
            gradingSuggestions: result.paletteAnalysis ? [result.suggestedCorrections.paletteCorrection?.monochrome
                    ? 'Black & White applies zero saturation. Export CUBE for the exact preview.'
                    : 'Apply the creative palette CUBE globally. This is colorization, not photographed-scene correspondence.',
                'Palette adherence is not a guarantee of artistic quality or a spatial skin grade.'] : this.suggestions.suggest(result),
        };
    }
}
function analyzer(frame, moduleId) {
    return frame.analyzers.find((result) => result.module_id === moduleId)?.metrics;
}
function mode(values, fallback) {
    if (!values.length)
        return fallback;
    const counts = new Map();
    values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? fallback;
}
function uniqueWarnings(frames) {
    const warnings = [];
    const seen = new Set();
    frames.flatMap((frame) => frame.warnings).forEach((warning) => {
        const key = `${warning.frame_number ?? ''}:${warning.module_id ?? ''}:${warning.code}:${warning.message}`;
        if (!seen.has(key)) {
            seen.add(key);
            warnings.push(warning);
        }
    });
    return warnings;
}
function worstFrames(frames, limit) {
    return frames.map((frame) => {
        const reasons = [];
        let score = frame.warnings.length * 0.12;
        const screen = analyzer(frame, 'screen-quality');
        if (screen) {
            score += (1 - screen.screen_quality_score) * 0.3;
            if (screen.screen_quality_score < 0.6)
                reasons.push('Low screen quality');
        }
        const spill = analyzer(frame, 'spill-detection');
        if (spill) {
            score += spill.spill_score * 0.25;
            if (spill.spill_risk === 'high')
                reasons.push('High spill risk');
        }
        const edge = analyzer(frame, 'edge-confidence');
        if (edge) {
            score += (1 - edge.final_edge_confidence_score) * 0.25;
            if (edge.final_edge_confidence_score < 0.5)
                reasons.push('Low edge confidence');
        }
        const exposure = analyzer(frame, 'exposure');
        if (exposure && exposure.exposure_status !== 'good') {
            score += 0.18;
            reasons.push(`Exposure: ${exposure.exposure_status}`);
        }
        if (frame.warnings.length)
            reasons.push(`${frame.warnings.length} warning${frame.warnings.length === 1 ? '' : 's'}`);
        return {
            frame_number: frame.frame_number,
            timestamp_ms: frame.timestamp_ms,
            score: round(Math.min(1, score)),
            warning_count: frame.warnings.length,
            reasons,
        };
    }).filter((frame) => frame.score > 0)
        .sort((a, b) => b.score - a.score || a.frame_number - b.frame_number)
        .slice(0, Math.max(0, limit));
}
export function createLimonadeEyeReport(frames, options = {}) {
    const screenScores = frames.map((frame) => analyzer(frame, 'screen-quality')?.screen_quality_score)
        .filter((value) => value !== undefined);
    const spillRisks = frames.map((frame) => analyzer(frame, 'spill-detection')?.spill_risk ?? 'unknown');
    const highestSpillRisk = spillRisks.reduce((highest, risk) => spillRank[risk] > spillRank[highest] ? risk : highest, 'unknown');
    const lightingPatterns = frames.map((frame) => analyzer(frame, 'lighting-setup')?.lighting_pattern)
        .filter((value) => value !== undefined);
    const edgeScores = frames.map((frame) => analyzer(frame, 'edge-confidence')?.final_edge_confidence_score)
        .filter((value) => value !== undefined);
    const exposureStatusSummary = {
        good: 0, underexposed: 0, overexposed: 0, too_contrasty: 0, too_flat: 0,
    };
    frames.forEach((frame) => {
        const status = analyzer(frame, 'exposure')?.exposure_status;
        if (status)
            exposureStatusSummary[status]++;
    });
    const warnings = uniqueWarnings(frames);
    const firstFrame = frames[0];
    return {
        schema: LIMONADE_EYE_REPORT_SCHEMA,
        session_id: options.sessionId ?? globalThis.crypto?.randomUUID?.() ?? `eye-${Date.now()}`,
        created_at: options.createdAt ?? new Date().toISOString(),
        source: {
            type: options.source?.type ?? 'video',
            name: options.source?.name ?? null,
            width: options.source?.width ?? firstFrame?.width ?? null,
            height: options.source?.height ?? firstFrame?.height ?? null,
            fps: options.source?.fps ?? null,
            duration_ms: options.source?.duration_ms ?? null,
        },
        summary: {
            frames_analyzed: frames.length,
            analyzer_modules: [...new Set(frames.flatMap((frame) => frame.analyzers.map((result) => result.module_id)))],
            average_screen_quality: average(screenScores),
            highest_spill_risk: highestSpillRisk,
            most_common_lighting_setup: mode(lightingPatterns, 'unknown'),
            average_edge_confidence: average(edgeScores),
            exposure_status_summary: exposureStatusSummary,
            total_warnings: warnings.length,
            warning_count: warnings.length,
            worst_frames: worstFrames(frames, options.worstFrameLimit ?? 5),
        },
        warnings,
        frames: [...frames],
    };
}
export class LimonadeEyeJSONExporter {
    id = 'limonade-eye-json';
    export(input) {
        const report = createLimonadeEyeReport(input.frames, input.options);
        return JSON.stringify(report, null, input.options?.pretty === false ? undefined : 2);
    }
}
function estimatesFromMotionLink(input) {
    if (Array.isArray(input)) {
        const estimates = input;
        return { sessionId: 'motionlink-estimates', estimates: [...estimates], source: estimates };
    }
    if ('estimates' in input) {
        return { sessionId: input.session_id, estimates: [...input.estimates], source: input };
    }
    const session = input;
    return {
        sessionId: session.session_id,
        estimates: session.samples.map((sample) => sample.camera_motion_estimate ?? {
            sample_type: 'camera_motion_estimate',
            timestamp_ms: sample.timestamp_ms,
            frame_number: sample.frame_number,
            rotation_quaternion: sample.rotation_quaternion,
            rotation_euler: sample.rotation_euler,
            acceleration: sample.acceleration,
            angular_velocity: sample.angular_velocity,
            position: sample.position,
            velocity: sample.velocity,
            tracking_confidence: sample.tracking_confidence,
            motion_type: sample.motion_type,
            confidence: sample.tracking_confidence,
            source: sample.arkit_pose && sample.imu ? 'fused' : sample.arkit_pose ? 'arkit' : sample.imu ? 'imu' : 'unknown',
        }),
        source: session,
    };
}
function csvValue(value) {
    if (value === null || value === undefined)
        return '';
    const text = String(value);
    return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}
function dominantMotionType(estimates) {
    if (!estimates.length)
        return 'unknown';
    const counts = new Map();
    estimates.forEach((estimate) => {
        counts.set(estimate.motion_type, (counts.get(estimate.motion_type) ?? 0) + 1);
    });
    return [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || motionPriority.indexOf(a[0]) - motionPriority.indexOf(b[0]))[0]?.[0] ?? 'unknown';
}
function totalEulerDeltaDegrees(estimates, axis) {
    const first = estimates.find((estimate) => estimate.rotation_euler)?.rotation_euler;
    const last = [...estimates].reverse().find((estimate) => estimate.rotation_euler)?.rotation_euler;
    if (!first || !last)
        return 0;
    return round(radiansToDegrees(last[axis] - first[axis]), 2);
}
function averageShakeIntensity(estimates) {
    if (!estimates.length)
        return 0;
    const angularSpeeds = estimates.map((estimate) => {
        const angular = estimate.angular_velocity;
        return angular ? Math.hypot(angular.x, angular.y, angular.z) : 0;
    });
    return round(Math.max(0, Math.min(1, angularSpeeds.reduce((sum, value) => sum + value, 0) / angularSpeeds.length / 2)), 2);
}
function averageNumber(values) {
    return values.length ? round(values.reduce((sum, value) => sum + value, 0) / values.length, 4) : 0;
}
function countBy(values) {
    const counts = new Map();
    values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1));
    return Object.fromEntries([...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])));
}
function dominantFromCounts(counts, fallback = 'unknown') {
    return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? fallback;
}
function speedDescriptor(estimates) {
    if (estimates.length < 2)
        return '';
    const first = estimates[0];
    const last = estimates[estimates.length - 1];
    const durationSeconds = Math.max(0.001, (last.timestamp_ms - first.timestamp_ms) / 1000);
    const panPerSecond = Math.abs(totalEulerDeltaDegrees(estimates, 'yaw')) / durationSeconds;
    if (panPerSecond > 80)
        return 'fast ';
    if (panPerSecond > 35)
        return 'quick ';
    if (panPerSecond < 8)
        return 'slow ';
    return '';
}
function estimateFps(estimates, fallback = 24) {
    if (estimates.length < 2)
        return fallback;
    const durations = estimates
        .slice(1)
        .map((estimate, index) => estimate.timestamp_ms - estimates[index].timestamp_ms)
        .filter((value) => Number.isFinite(value) && value > 0);
    if (!durations.length)
        return fallback;
    return round(1000 / averageNumber(durations), 3);
}
function cameraExportMetadata(input, format, options = {}) {
    const { sessionId, estimates } = estimatesFromMotionLink(input);
    return {
        schema: 'limonade-motionlink-camera-export/v1',
        source_schema: LIMONADE_MOTIONLINK_SCHEMA,
        session_id: sessionId,
        format,
        camera_name: options.cameraName ?? 'MotionLinkCamera',
        frame_count: estimates.length,
        fps: options.fps ?? estimateFps(estimates),
        unit: format === 'fbx-ascii' ? 'centimeters' : 'meters',
        limitations: [
            'MotionLink v0.1 exports approximate camera path estimates only.',
            'This is not a solved matchmove, lens solve, SLAM reconstruction, or production-calibrated camera track.',
            'Lens, filmback, distortion, focus, and rolling-shutter compensation are placeholders.',
        ],
    };
}
function exportTimeCode(estimate, index, fps) {
    if (estimate.frame_number !== undefined)
        return estimate.frame_number;
    return round((estimate.timestamp_ms / 1000) * fps || index + 1, 4);
}
function exportPosition(estimate, unitScale = 1) {
    const position = estimate.position ?? { x: 0, y: 0, z: 0 };
    return [round(position.x * unitScale, 6), round(position.y * unitScale, 6), round(position.z * unitScale, 6)];
}
function exportRotationDegrees(estimate) {
    const rotation = estimate.rotation_euler;
    return rotation
        ? [round(radiansToDegrees(rotation.pitch), 6), round(radiansToDegrees(rotation.yaw), 6), round(radiansToDegrees(rotation.roll), 6)]
        : [0, 0, 0];
}
function fbxTime(estimate) {
    return Math.round((estimate.timestamp_ms / 1000) * FBX_TIME_TICKS_PER_SECOND);
}
function fbxArray(values, formatter = (value) => String(value)) {
    return `*${values.length} {\n        a: ${values.map(formatter).join(',')}\n      }`;
}
function fbxAnimationCurve(id, values, times) {
    const flags = values.map(() => 24836);
    const attr = values.flatMap(() => [0, 0, 9.999999682655225e+37, 9.999999682655225e+37]);
    const refs = values.map(() => 1);
    return `    AnimationCurve: ${id}, \"AnimCurve::\", \"\" {
      Default: 0
      KeyVer: 4008
      KeyTime: ${fbxArray(times)}
      KeyValueFloat: ${fbxArray(values, (value) => round(value, 6).toFixed(6))}
      KeyAttrFlags: ${fbxArray(flags)}
      KeyAttrDataFloat: ${fbxArray(attr, (value) => String(value))}
      KeyAttrRefCount: ${fbxArray(refs)}
    }`;
}
export function exportMotionLinkJSON(input, options = {}) {
    const { source, sessionId, estimates } = estimatesFromMotionLink(input);
    const payload = Array.isArray(source) ? {
        schema: LIMONADE_MOTIONLINK_SCHEMA,
        session_id: sessionId,
        estimates,
    } : source;
    return JSON.stringify(payload, null, options.pretty === false ? undefined : 2);
}
export function exportMotionLinkCSV(input) {
    const { estimates } = estimatesFromMotionLink(input);
    const headers = [
        'frame_number', 'timestamp_ms', 'motion_type', 'confidence', 'tracking_confidence',
        'pitch_degrees', 'yaw_degrees', 'roll_degrees',
        'position_x', 'position_y', 'position_z',
        'velocity_x', 'velocity_y', 'velocity_z',
        'source',
    ];
    const rows = estimates.map((estimate, index) => [
        estimate.frame_number ?? index + 1,
        estimate.timestamp_ms,
        estimate.motion_type,
        round(estimate.confidence),
        round(estimate.tracking_confidence),
        estimate.rotation_euler ? round(radiansToDegrees(estimate.rotation_euler.pitch), 3) : null,
        estimate.rotation_euler ? round(radiansToDegrees(estimate.rotation_euler.yaw), 3) : null,
        estimate.rotation_euler ? round(radiansToDegrees(estimate.rotation_euler.roll), 3) : null,
        estimate.position?.x ?? null,
        estimate.position?.y ?? null,
        estimate.position?.z ?? null,
        estimate.velocity?.x ?? null,
        estimate.velocity?.y ?? null,
        estimate.velocity?.z ?? null,
        estimate.source,
    ]);
    return [headers, ...rows].map((row) => row.map(csvValue).join(',')).join('\n');
}
export function createSimplifiedCameraPathJSON(input) {
    const { estimates, sessionId } = estimatesFromMotionLink(input);
    return {
        schema: 'limonade-motionlink-camera-path/v1',
        source_schema: LIMONADE_MOTIONLINK_SCHEMA,
        session_id: sessionId,
        frame_count: estimates.length,
        frames: estimates.map((estimate, index) => ({
            frame_number: estimate.frame_number ?? index + 1,
            timestamp_ms: estimate.timestamp_ms,
            motion_type: estimate.motion_type,
            confidence: round(estimate.confidence),
            rotation_degrees: {
                pitch: estimate.rotation_euler ? round(radiansToDegrees(estimate.rotation_euler.pitch), 3) : null,
                yaw: estimate.rotation_euler ? round(radiansToDegrees(estimate.rotation_euler.yaw), 3) : null,
                roll: estimate.rotation_euler ? round(radiansToDegrees(estimate.rotation_euler.roll), 3) : null,
            },
            position: estimate.position,
        })),
    };
}
export function exportSimplifiedCameraPathJSON(input, options = {}) {
    return JSON.stringify(createSimplifiedCameraPathJSON(input), null, options.pretty === false ? undefined : 2);
}
export function createAIVideoPromptHelper(input) {
    const { estimates } = estimatesFromMotionLink(input);
    const type = dominantMotionType(estimates);
    const panDegrees = totalEulerDeltaDegrees(estimates, 'yaw');
    const tiltDegrees = totalEulerDeltaDegrees(estimates, 'pitch');
    const rollDegrees = totalEulerDeltaDegrees(estimates, 'roll');
    const shakeIntensity = averageShakeIntensity(estimates);
    const hasHandheld = estimates.some((estimate) => estimate.motion_type === 'handheld' || estimate.motion_type === 'shake') ||
        shakeIntensity > 0.12;
    const slightPan = Math.abs(panDegrees) >= 1.5 && Math.abs(panDegrees) < 12;
    const panText = slightPan ? ` with slight pan ${panDegrees >= 0 ? 'right' : 'left'}` : '';
    const speed = speedDescriptor(estimates);
    let motionPrompt;
    if (type === 'static') {
        motionPrompt = 'locked-off tripod shot';
    }
    else if (type === 'pan_left' && speed === 'fast ') {
        motionPrompt = 'fast whip pan left';
    }
    else if (type === 'pan_right' && speed === 'fast ') {
        motionPrompt = 'fast whip pan right';
    }
    else if (type === 'push_in') {
        motionPrompt = `${speed}${hasHandheld ? 'handheld ' : ''}push-in${panText}`.trim();
    }
    else if (type === 'pull_out') {
        motionPrompt = `${speed}${hasHandheld ? 'handheld ' : ''}pull-out${panText}`.trim();
    }
    else if (type === 'shake') {
        motionPrompt = Math.abs(tiltDegrees) > Math.abs(panDegrees)
            ? 'subtle handheld vertical shake'
            : 'subtle handheld camera shake';
    }
    else if (type === 'orbit_left' || type === 'orbit_right') {
        motionPrompt = type === 'orbit_right'
            ? 'orbit move around subject from left to right'
            : 'orbit move around subject from right to left';
    }
    else if (type === 'handheld') {
        motionPrompt = `subtle handheld movement${panText}`.trim();
    }
    else {
        motionPrompt = `${speed}${type.replaceAll('_', ' ')}${panText}`.trim();
    }
    return {
        motion_prompt: motionPrompt,
        camera_movement: {
            type,
            pan_degrees: panDegrees,
            tilt_degrees: tiltDegrees,
            roll_degrees: rollDegrees,
            shake_intensity: shakeIntensity,
        },
    };
}
export function createMotionLinkAIVideoAudit(result) {
    const comparisons = result.optical_flow_comparisons;
    const intendedCounts = countBy(comparisons.map((comparison) => comparison.motionlink_motion_type));
    const visualCounts = countBy(comparisons.map((comparison) => comparison.optical_flow_motion_type));
    const intendedDominant = dominantFromCounts(intendedCounts, result.ai_motion_prompt.camera_movement.type);
    const visualDominant = dominantFromCounts(visualCounts);
    const comparisonCount = comparisons.length;
    const disagreementCount = comparisons.filter((comparison) => comparison.warning).length;
    const directionAgreement = averageNumber(comparisons.map((comparison) => comparison.direction_agreement));
    const magnitudeAgreement = averageNumber(comparisons.map((comparison) => comparison.magnitude_agreement));
    const promptAdherence = averageNumber(comparisons.map((comparison) => comparison.agreement_score));
    const visualConfidence = averageNumber(comparisons.map((comparison) => comparison.confidence_agreement));
    const intendedSignificant = Object.entries(intendedCounts)
        .filter(([, count]) => count / Math.max(1, comparisonCount) >= 0.08)
        .map(([type]) => type);
    const visualSignificant = Object.entries(visualCounts)
        .filter(([, count]) => count / Math.max(1, comparisonCount) >= 0.08)
        .map(([type]) => type);
    const missingMotion = intendedSignificant
        .filter((type) => type !== 'unknown' && type !== 'static' && !(type in visualCounts))
        .map((type) => `Expected ${type.replaceAll('_', ' ')} but it was not clearly visible in optical flow.`);
    const extraMotion = visualSignificant
        .filter((type) => type !== 'unknown' && type !== 'static' && !(type in intendedCounts))
        .map((type) => `Generated video shows extra ${type.replaceAll('_', ' ')} motion.`);
    const warnings = [
        ...(comparisonCount === 0 ? ['No optical-flow comparisons available; load/analyze the generated video before auditing.'] : []),
        ...(disagreementCount > comparisonCount * 0.35 ? ['Generated video motion does not closely follow the MotionLink prompt.'] : []),
        ...(intendedCounts.roll ? ['Roll is only partially observable with OpticalFlowAnalyzer v0.1; treat roll disagreement as advisory.'] : []),
    ];
    const recommendations = [
        ...(missingMotion.length ? missingMotion.map((message) => message.replace('Expected', 'Emphasize')) : []),
        ...(extraMotion.length ? extraMotion.map((message) => message.replace('Generated video shows extra', 'Reduce unintended')) : []),
        ...(promptAdherence < 0.55 && intendedDominant !== 'unknown'
            ? [`Regenerate with a clearer camera-motion phrase: \"${result.ai_motion_prompt.motion_prompt}\".`]
            : []),
        ...(visualDominant === 'push_in' && intendedDominant !== 'push_in'
            ? ['The generated video appears to push in; add or promote push-in only if the MotionLink path intentionally moves forward.']
            : []),
    ];
    return {
        schema: 'limonade-motionlink-ai-video-audit/v1',
        source_schema: LIMONADE_MOTIONLINK_SCHEMA,
        session_id: result.session_id,
        intended_motion_prompt: result.ai_motion_prompt.motion_prompt,
        intended_motion: {
            dominant_type: intendedDominant,
            motion_counts: intendedCounts,
        },
        actual_visual_motion: {
            dominant_type: visualDominant,
            motion_counts: visualCounts,
            average_confidence: round(visualConfidence),
        },
        prompt_adherence_score: round(promptAdherence),
        direction_agreement_score: round(directionAgreement),
        magnitude_agreement_score: round(magnitudeAgreement),
        comparison_count: comparisonCount,
        disagreement_rate: round(comparisonCount ? disagreementCount / comparisonCount : 0),
        missing_motion: missingMotion,
        extra_motion: extraMotion,
        recommendations: [...new Set(recommendations)],
        warnings: [...new Set(warnings)],
        limitations: [
            'This audits generated video pixels against MotionLink intent using 2D optical flow.',
            'It does not prove physical camera accuracy, 3D matchmove accuracy, lens solve quality, or AI model compliance.',
            'Roll, orbit, and complex parallax are approximate in OpticalFlowAnalyzer v0.1.',
        ],
    };
}
export function exportMotionLinkUSD(input, options = {}) {
    const { estimates, sessionId } = estimatesFromMotionLink(input);
    const metadata = cameraExportMetadata(input, 'usda', options);
    const fps = options.fps ?? metadata.fps;
    const cameraName = (options.cameraName ?? 'MotionLinkCamera').replaceAll(/[^A-Za-z0-9_]/g, '_');
    const focalLength = options.focalLengthMm ?? 35;
    const translateSamples = estimates.map((estimate, index) => {
        const [x, y, z] = exportPosition(estimate, options.unitScale ?? 1);
        return `        ${exportTimeCode(estimate, index, fps)}: (${x}, ${y}, ${z})`;
    });
    const rotateSamples = estimates.map((estimate, index) => {
        const [pitch, yaw, rollValue] = exportRotationDegrees(estimate);
        return `        ${exportTimeCode(estimate, index, fps)}: (${pitch}, ${yaw}, ${rollValue})`;
    });
    return `#usda 1.0
(
    defaultPrim = \"${cameraName}\"
    metersPerUnit = 1
    upAxis = \"Y\"
    timeCodesPerSecond = ${fps}
    framesPerSecond = ${fps}
    startTimeCode = ${estimates.length ? exportTimeCode(estimates[0], 0, fps) : 0}
    endTimeCode = ${estimates.length ? exportTimeCode(estimates[estimates.length - 1], estimates.length - 1, fps) : 0}
)

def Camera \"${cameraName}\"
{
    custom string motionLinkSchema = \"${metadata.schema}\"
    custom string sourceSchema = \"${metadata.source_schema}\"
    custom string sessionId = \"${sessionId}\"
    custom string limitation = \"MotionLink v0.1 exports approximate local camera-path estimates, not a solved matchmove.\"
    float focalLength = ${focalLength}
    double3 xformOp:translate.timeSamples = {
${translateSamples.join(',\n')}
    }
    float3 xformOp:rotateXYZ.timeSamples = {
${rotateSamples.join(',\n')}
    }
    uniform token[] xformOpOrder = [\"xformOp:translate\", \"xformOp:rotateXYZ\"]
}
`;
}
export function exportMotionLinkFBX(input, options = {}) {
    const { estimates, sessionId } = estimatesFromMotionLink(input);
    const metadata = cameraExportMetadata(input, 'fbx-ascii', options);
    const cameraName = options.cameraName ?? 'MotionLinkCamera';
    const unitScale = options.unitScale ?? 100;
    const focalLength = options.focalLengthMm ?? 35;
    const times = estimates.map(fbxTime);
    const translations = estimates.map((estimate) => exportPosition(estimate, unitScale));
    const rotations = estimates.map(exportRotationDegrees);
    const channels = [
        { id: 4000, node: 3000, property: 'd|X', values: translations.map((value) => value[0]) },
        { id: 4001, node: 3000, property: 'd|Y', values: translations.map((value) => value[1]) },
        { id: 4002, node: 3000, property: 'd|Z', values: translations.map((value) => value[2]) },
        { id: 4003, node: 3001, property: 'd|X', values: rotations.map((value) => value[0]) },
        { id: 4004, node: 3001, property: 'd|Y', values: rotations.map((value) => value[1]) },
        { id: 4005, node: 3001, property: 'd|Z', values: rotations.map((value) => value[2]) },
    ];
    return `; FBX 7.4.0 ASCII generated by Limonade EYE MotionLink v0.1
; Session: ${sessionId}
; Limitation: approximate MotionLink camera path, not a solved matchmove/lens solve.
FBXHeaderExtension:  {
  FBXHeaderVersion: 1003
  FBXVersion: 7400
  Creator: \"Limonade EYE MotionLink\"
}
GlobalSettings:  {
  Version: 1000
  Properties70:  {
    P: \"UpAxis\", \"int\", \"Integer\", \"\",1
    P: \"UpAxisSign\", \"int\", \"Integer\", \"\",1
    P: \"FrontAxis\", \"int\", \"Integer\", \"\",2
    P: \"FrontAxisSign\", \"int\", \"Integer\", \"\",1
    P: \"CoordAxis\", \"int\", \"Integer\", \"\",0
    P: \"CoordAxisSign\", \"int\", \"Integer\", \"\",1
    P: \"UnitScaleFactor\", \"double\", \"Number\", \"\",1
    P: \"OriginalUnitScaleFactor\", \"double\", \"Number\", \"\",1
    P: \"TimeMode\", \"enum\", \"\", \"\",6
  }
}
Documents:  {
  Count: 1
  Document: 123456789, \"\", \"Scene\" {
    Properties70:  {
      P: \"SourceObject\", \"object\", \"\", \"\"
      P: \"ActiveAnimStackName\", \"KString\", \"\", \"\", \"MotionLinkTake\"
    }
  }
}
Definitions:  {
  Version: 100
  Count: 12
  ObjectType: \"Model\" { Count: 1 }
  ObjectType: \"NodeAttribute\" { Count: 1 }
  ObjectType: \"AnimationStack\" { Count: 1 }
  ObjectType: \"AnimationLayer\" { Count: 1 }
  ObjectType: \"AnimationCurveNode\" { Count: 2 }
  ObjectType: \"AnimationCurve\" { Count: 6 }
}
Objects:  {
  Model: 1000, \"Model::${cameraName}\", \"Camera\" {
    Version: 232
    Properties70:  {
      P: \"Lcl Translation\", \"Lcl Translation\", \"\", \"A\",0,0,0
      P: \"Lcl Rotation\", \"Lcl Rotation\", \"\", \"A\",0,0,0
      P: \"Lcl Scaling\", \"Lcl Scaling\", \"\", \"A\",1,1,1
      P: \"MotionLinkSchema\", \"KString\", \"\", \"U\", \"${metadata.schema}\"
      P: \"MotionLinkSourceSchema\", \"KString\", \"\", \"U\", \"${metadata.source_schema}\"
      P: \"MotionLinkSessionId\", \"KString\", \"\", \"U\", \"${sessionId}\"
    }
  }
  NodeAttribute: 1001, \"NodeAttribute::${cameraName}\", \"Camera\" {
    TypeFlags: \"Camera\"
    Properties70:  {
      P: \"FocalLength\", \"double\", \"Number\", \"A\",${focalLength}
      P: \"CameraProjectionType\", \"enum\", \"\", \"\",0
    }
  }
  AnimationStack: 2000, \"AnimStack::MotionLinkTake\", \"\" {}
  AnimationLayer: 2001, \"AnimLayer::BaseLayer\", \"\" {}
  AnimationCurveNode: 3000, \"AnimCurveNode::T\", \"\" {
    Properties70:  {
      P: \"d|X\", \"Number\", \"\", \"A\",0
      P: \"d|Y\", \"Number\", \"\", \"A\",0
      P: \"d|Z\", \"Number\", \"\", \"A\",0
    }
  }
  AnimationCurveNode: 3001, \"AnimCurveNode::R\", \"\" {
    Properties70:  {
      P: \"d|X\", \"Number\", \"\", \"A\",0
      P: \"d|Y\", \"Number\", \"\", \"A\",0
      P: \"d|Z\", \"Number\", \"\", \"A\",0
    }
  }
${channels.map((channel) => fbxAnimationCurve(channel.id, channel.values, times)).join('\n')}
}
Connections:  {
  C: \"OO\",1001,1000
  C: \"OO\",2001,2000
  C: \"OO\",3000,2001
  C: \"OO\",3001,2001
  C: \"OP\",3000,1000,\"Lcl Translation\"
  C: \"OP\",3001,1000,\"Lcl Rotation\"
${channels.map((channel) => `  C: \"OP\",${channel.id},${channel.node},\"${channel.property}\"`).join('\n')}
}
`;
}
export function createMotionLinkCameraPathExportMetadata(input, format, options = {}) {
    return cameraExportMetadata(input, format, options);
}
export class MotionLinkJSONExporter {
    id = 'motionlink-json';
    export(input) {
        return exportMotionLinkJSON(input);
    }
}
export class MotionLinkCSVExporter {
    id = 'motionlink-csv';
    export(input) {
        return exportMotionLinkCSV(input);
    }
}
export class SimplifiedCameraPathJSONExporter {
    id = 'motionlink-camera-path-json';
    export(input) {
        return exportSimplifiedCameraPathJSON(input);
    }
}
export class AIVideoPromptHelperExporter {
    id = 'motionlink-ai-video-prompt-helper';
    export(input) {
        return createAIVideoPromptHelper(input);
    }
}
export class MotionLinkAIVideoAuditExporter {
    id = 'motionlink-ai-video-audit';
    export(input) {
        return createMotionLinkAIVideoAudit(input);
    }
}
export class MotionLinkUSDExporter {
    id = 'motionlink-usd-camera-path';
    export(input) {
        return exportMotionLinkUSD(input);
    }
}
export class MotionLinkFBXExporter {
    id = 'motionlink-fbx-camera-path';
    export(input) {
        return exportMotionLinkFBX(input);
    }
}
